<script setup lang="ts">
import dayjs from 'dayjs';
import DownloadFileIcon from '@/assets/images/svg/download-file.svg?component';
import TrashcanIcon from '@/assets/images/svg/trashcan.svg?component';
import LinkIcon from '@/assets/images/svg/link.svg?component';
import CheckIcon from '@/assets/images/svg/check.svg?component';
import HideEyeIcon from '@/assets/images/svg/hide-eye.svg?component';
import ShowEyeIcon from '@/assets/images/svg/show-eye.svg?component';
import ReportActionButton from '@/_core/entities/report/ui/ReportActionButton/ReportActionButton.vue';
import { TReport } from '../../models/types';
import { computed, ref } from 'vue';

const props = defineProps<{
  reportData: TReport;
  isSingleScan: boolean;
  isDownloading: boolean;
  isTogglingShared: boolean;
}>();

const emit = defineEmits<{
  (e: 'download'): void;
  (e: 'toggleShared', shared: boolean): void;
}>();

const scanCompletionDateTime = computed(() => {
  if (props.reportData?.completionTime) {
    return dayjs(props.reportData.completionTime).format('DD MMM YYYY [at] HH:mm');
  }

  return 'N/A';
});

const isCopied = ref(false);

async function copyLink() {
  await navigator.clipboard.writeText(window.location.href);
  isCopied.value = true;
  setTimeout(() => (isCopied.value = false), 2000);
}
</script>

<template>
  <div class="tw:md:flex tw:md:justify-between tw:md:items-start">
    <div>
      <div class="tw:lg:pt-1.5 tw:typography-h1">Scan Report</div>

      <div class="tw:mt-5 tw:typography-small tw:text-ghost">
        Scan Completed:
        <span class="tw:font-medium">{{ scanCompletionDateTime }}</span>
      </div>

      <div class="tw:typography-legacy-title">{{ reportData.fileName }}</div>

      <div class="tw:mt-5 tw:flex tw:gap-5">
        <div class="tw:flex tw:flex-col tw:gap-2.5 tw:typography-small tw:text-ghost">
          <div>App ID</div>
          <div class="tw:text-secondary">{{ reportData.appId }}</div>
        </div>

        <div class="tw:flex tw:flex-col tw:gap-2.5 tw:typography-small tw:text-ghost">
          <div>Version</div>
          <div class="tw:text-secondary">{{ reportData.version }}</div>
        </div>

        <div class="tw:flex tw:flex-col tw:gap-2.5 tw:typography-small tw:text-ghost">
          <div>Platform</div>
          <div class="tw:text-secondary tw:capitalize">
            {{ reportData.platform }}
          </div>
        </div>
      </div>
    </div>

    <div class="tw:max-md:mt-5 tw:flex tw:gap-2">
      <ReportActionButton
        :icon="DownloadFileIcon"
        :loading="isDownloading"
        @click="emit('download')"
      />
      <ReportActionButton
        :icon="isCopied ? CheckIcon : LinkIcon"
        :label="isCopied ? 'Link copied' : undefined"
        @click="copyLink"
      />
      <ReportActionButton :icon="TrashcanIcon" />
      <ReportActionButton
        v-if="reportData.isOwner && isSingleScan"
        :icon="reportData.shared ? HideEyeIcon : ShowEyeIcon"
        :disabled="isTogglingShared"
        @click="emit('toggleShared', !reportData.shared)"
      />
    </div>
  </div>
</template>
